import React, { useEffect } from "react";
import { useTranslation } from "@lib/i18n";
import { func, string, bool } from "prop-types";
import { useRouter } from "next/router";

import { Button } from "@components/shared/Button";
import AddressTextField from "@components/shared/AddressTextField";
import SimpleButton from "@components/shared/SimpleButton";
import Typography from "@components/shared/Typography";
import gtm from "@gtm/core";

import { addressType } from "@types";
import { revertProgressEvent } from "@gtm/events/shared";
import { basketDeliveryLocationUpdateUrlEvent } from "@gtm/events/basket";
import { useUserProfileSelector } from "@redux/reducers/deal";
import { useIsB2BSelected } from "@hooks/useIsB2BSelected";
import { STEPS } from "@gtm/constants";
import { DELIVERY_METHOD, GTM_TYPES } from "@shared/constants";

const AddressSelection = ({
  affiliation,
  titleLabel = "",
  pageVariant,
  inputMapAddress,
  setDeliveryAddress,
  onNextState,
  onPrevState,
  setInputMapAddress,
  setLatLng,
  isSSRRendered,
}) => {
  const { t } = useTranslation();
  const { deliveryData } = useUserProfileSelector();
  const isB2BSelected = useIsB2BSelected();
  const router = useRouter();

  const isAddressValid = inputMapAddress?.geoLat && inputMapAddress?.geoLng;
  const { HOME } = DELIVERY_METHOD;
  const { SHOP } = GTM_TYPES;
  const deliveryType = deliveryData.type === HOME ? HOME : SHOP;

  useEffect(() => {
    gtm.fire(
      basketDeliveryLocationUpdateUrlEvent({
        pageTitle: router.asPath,
        deliveryType,
        pageVariant,
        affiliation,
        isB2BSelected,
        isSSRRendered,
      })
    );
  }, []);

  const handleSetAddress = address => {
    const { geoLat: lat, geoLng: lng } = address;
    setDeliveryAddress(address);
    setLatLng({ lat, lng });
    setInputMapAddress(address);
  };

  const handleResetAddress = () => {
    setDeliveryAddress(null);
  };

  return (
    <div className="flex flex-col justify-between gap-4 w-full">
      <SimpleButton
        className="px-0 py-2 m-0"
        dataId="delivery-back-button"
        onClick={() => {
          onPrevState();
          gtm.fire(revertProgressEvent(STEPS.DELIVERY));
        }}
        withArrow
      >
        {t("basket.back")}
      </SimpleButton>
      <Typography className="text-sm">
        {t("basket.delivery.description")}
      </Typography>
      <Typography className="uppercase font-semibold">
        {t("basket.delivery.selectTitle")}
      </Typography>
      <Typography className="font-semibold">{titleLabel}</Typography>
      <AddressTextField
        initialValue={inputMapAddress}
        onSetAddress={handleSetAddress}
        onResetAddress={handleResetAddress}
      />
      <div className="flex justify-center items-center w-full py-4">
        <Button
          data-id="delivery-address-selection-continue"
          disabled={!isAddressValid}
          onClick={onNextState}
        >
          {t("basket.delivery.continue")}
        </Button>
      </div>
    </div>
  );
};

AddressSelection.propTypes = {
  affiliation: string,
  titleLabel: string,
  pageVariant: string.isRequired,
  inputMapAddress: addressType,
  setDeliveryAddress: func.isRequired,
  onNextState: func,
  onPrevState: func,
  setInputMapAddress: func.isRequired,
  setLatLng: func.isRequired,
  isSSRRendered: bool,
};

export default AddressSelection;
